import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/authcontext';
import AdminSideNav from './adminsidenav';
import AdminNav from './adminnav';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openMenus, setOpenMenus] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);

      if (width < 768) {
        setSidebarOpen(false);
      } else if (width >= 1024) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // ============================================
  // ALL NAVIGATION ITEMS (Full Menu)
  // ============================================
  const allNavigationItems = [
    {
      label: 'Dashboard',
      path: '/admin/dashboard',
      icon: '📊',
      hasPermission: true
    },
    {
      label: 'Stay Overview',
      path: '/admin/stay-overview',
      icon: '🛏️',
      hasPermission: true
    },
    {
      label: 'Front Office',
      icon: '🏨',
      hasPermission: true,
      children: [
        { label: 'New Reservation', path: '/admin/reservations', icon: '📝', description: 'Create New Booking' },
        { label: 'Operations', path: '/admin/front-office/operations', icon: '⚡', description: 'Reservations & Check-ins' },
        { label: 'Stay Overview', path: '/admin/stay-overview', icon: '🛏️', description: 'Current Status' },
        { label: 'Availability', path: '/admin/front-office/availability', icon: '📅', description: 'Room Status' },
        { label: 'Forecast', path: '/admin/availability-forecast', icon: '📊', description: '6-Month Forecast' },
        { label: 'Billing', path: '/admin/front-office/billing', icon: '💳', description: 'Guest Billing & Invoices' },
        { label: 'Billing Reports', path: '/admin/billing-reports', icon: '📊', description: 'GST & Revenue Analysis' },
        { label: 'Day Settlement', path: '/admin/day-settlement', icon: '📋', description: 'Daily Revenue Report' },
        { label: 'Monthly Sales', path: '/admin/monthly-sales', icon: '📈', description: 'Monthly GST Report' },
        { label: 'Night Audit', path: '/admin/night-audit', icon: '🌙', description: 'End of Day Audit' },
        { label: 'Views', path: '/admin/front-office/views', icon: '👁️', description: 'Guest Management' },
        { label: 'Reports', path: '/admin/front-office/reports', icon: '📈', description: 'Analytics & Reports' },
        { label: 'Setup', path: '/admin/front-office/setup', icon: '⚙️', description: 'Configuration' },
        { label: 'Settings', path: '/admin/front-office/settings', icon: '🔧', description: 'Preferences' }
      ]
    },
    {
      label: 'House Keeping',
      icon: '🧹',
      hasPermission: true,
      children: [
        { label: 'Operations', path: '/admin/house-keeping/operations', icon: '📋', description: 'Daily Tasks & Cleaning' },
        { label: 'Setup', path: '/admin/house-keeping/setup', icon: '⚙️', description: 'Staff & Categories' },
        { label: 'Settings', path: '/admin/house-keeping/settings', icon: '🔧', description: 'Configuration' }
      ]
    },
    {
      label: 'Point Of Sale',
      icon: '🛒',
      hasPermission: true,
      children: [
        { label: 'POS Dashboard', path: '/admin/pos/dashboard', icon: '📊', description: 'Sales Overview' },
        { label: 'POS Main', path: '/admin/pos/main', icon: '💳', description: 'Billing Interface' },
        { label: 'Operations', path: '/admin/pos/operations', icon: '🍽️', description: 'Tables & Kitchen' },
        { label: 'Setup', path: '/admin/pos/setup', icon: '⚙️', description: 'Items & Menu' }
      ]
    },
    {
      label: 'Kitchen & Restaurant',
      icon: '👨‍🍳',
      hasPermission: true,
      children: [
        { label: 'Operations', path: '/admin/kitchen/operations', icon: '🔥', description: 'Orders & Cooking' },
        { label: 'Menu Management', path: '/admin/kitchen/menu', icon: '📋', description: 'Menu Items & Pricing' },
        { label: 'Inventory', path: '/admin/kitchen/inventory', icon: '📦', description: 'Ingredients & Stock' },
        { label: 'Reports', path: '/admin/kitchen/reports', icon: '📊', description: 'Sales & Analytics' },
        { label: 'Setup', path: '/admin/kitchen/setup', icon: '⚙️', description: 'Stations & Equipment' }
      ]
    },
    {
      label: 'Bar Management',
      icon: '🍸',
      hasPermission: true,
      children: [
        { label: 'Main', path: '/admin/bar/main', icon: '🏠', description: 'Bar Dashboard' },
        { label: 'Operations', path: '/admin/bar/operations', icon: '🥃', description: 'Orders & Service' },
        { label: 'Inventory', path: '/admin/bar/inventory', icon: '🍾', description: 'Beverages & Stock' },
        { label: 'Menu', path: '/admin/bar/menu', icon: '📋', description: 'Drinks & Cocktails' },
        { label: 'Reports', path: '/admin/bar/reports', icon: '📊', description: 'Sales & Analytics' },
        { label: 'Setup', path: '/admin/bar/setup', icon: '⚙️', description: 'Bar Configuration' }
      ]
    },
    {
      label: 'Banquet',
      icon: '🎉',
      hasPermission: true,
      children: [
        { label: 'Operations', path: '/admin/banquet/operations', icon: '📅', description: 'Bookings & Events' },
        { label: 'Reports', path: '/admin/banquet/reports', icon: '📊', description: 'Analytics' },
        { label: 'Setup', path: '/admin/banquet/setup', icon: '⚙️', description: 'Packages & Items' }
      ]
    },
    {
      label: 'Stores',
      icon: '📦',
      hasPermission: true,
      children: [
        { label: 'Operations', path: '/admin/stores/operations', icon: '📝', description: 'Inventory Management' },
        { label: 'Reports', path: '/admin/stores/reports', icon: '📈', description: 'Stock Analytics' },
        { label: 'Setup', path: '/admin/stores/setup', icon: '⚙️', description: 'Items & Suppliers' }
      ]
    },
    {
      label: 'Accounts',
      icon: '💰',
      hasPermission: true,
      children: [
        { label: 'Operations', path: '/admin/accounts/operations', icon: '💳', description: 'Transactions' },
        { label: 'Reports', path: '/admin/accounts/reports', icon: '📊', description: 'Financial Reports' },
        { label: 'Profit & Loss', path: '/admin/profit-loss', icon: '📉', description: 'P&L Statement' },
        { label: 'Setup', path: '/admin/accounts/setup', icon: '⚙️', description: 'Chart of Accounts' }
      ]
    },
    {
      label: 'Channel Manager',
      icon: '🌐',
      hasPermission: true,
      children: [
        { label: 'Operations', path: '/admin/channel-manager/operations', icon: '📡', description: 'OTA Management' },
        { label: 'Settings', path: '/admin/channel-manager/settings', icon: '🔧', description: 'Configuration' }
      ]
    },
    {
      label: 'Setup',
      icon: '⚙️',
      hasPermission: true,
      children: [
        { label: 'System Setup', path: '/admin/setup/system', icon: '🏨', description: 'Hotel Configuration' },
        { label: 'Communication', path: '/admin/setup/communication', icon: '📧', description: 'Templates & Backup' }
      ]
    },
    {
      label: 'Administration',
      icon: '🛡️',
      hasPermission: true,
      children: [
        { label: 'System Admin', path: '/admin/administration/system', icon: '👨‍💼', description: 'Users & Roles' },
        { label: 'Integrations', path: '/admin/administration/integrations', icon: '🔌', description: 'Third-party Apps' }
      ]
    }
  ];

  // ============================================
  // FILTER NAVIGATION BASED ON USER ROLE
  // This ONLY hides menu items - does NOT block routes
  // ============================================
  const getFilteredNavigation = () => {
    const allowedMenus = user?.allowedMenus || [];

    // Admin sees everything
    if (user?.roleCode === 'admin') {
      return allNavigationItems;
    }

    // If no allowedMenus defined, show all (backward compatibility)
    if (!allowedMenus || allowedMenus.length === 0) {
      return allNavigationItems;
    }

    // Filter to show only allowed menus
    return allNavigationItems.filter(item => allowedMenus.includes(item.label));
  };

  const navigationItems = getFilteredNavigation();

  // Auto-open menus based on current path
  useEffect(() => {
    const currentPath = window.location.pathname;
    const newOpenMenus = {};

    navigationItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => child.path === currentPath);
        if (hasActiveChild) {
          newOpenMenus[item.label] = true;
        }
      }
    });

    setOpenMenus(prev => ({ ...prev, ...newOpenMenus }));
  }, [window.location.pathname]);

  // User info
  const userInfo = {
    email: user?.username || 'admin@hotel.com',
    role: user?.roleName || (user?.role === 'admin' ? 'Administrator' : 'User'),
    name: user?.fullName || user?.username || 'User',
    roleCode: user?.roleCode || 'guest'
  };

  // Colors
  const colors = {
    primary: '#2D5016',
    primaryLight: '#3d6b1f',
    primaryDark: '#1f3a0f',
    secondary: '#4a7c2a',
    accent: '#6ba83e',
    white: '#ffffff',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6'
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const handleOverlayClick = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      background: colors.lightGray,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      marginLeft: isMobile ? '0' : (sidebarOpen ? '290px' : '76px'),
      transition: 'margin-left 0.3s ease',
      minHeight: '100vh',
      width: isMobile ? '100%' : 'auto'
    },
    contentArea: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: isMobile ? '16px' : (isTablet ? '20px' : '28px'),
      background: colors.lightGray,
      minHeight: 'calc(100vh - 68px)'
    },
    overlay: {
      display: isMobile && sidebarOpen ? 'block' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
      transition: 'opacity 0.3s'
    }
  };

  if (!user) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '18px',
        color: colors.primary,
        background: colors.lightGray
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏨</div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Mobile Overlay */}
      <div style={styles.overlay} onClick={handleOverlayClick}></div>

      {/* Sidebar */}
      <AdminSideNav
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        openMenus={openMenus}
        setOpenMenus={setOpenMenus}
        darkMode={false}
        user={userInfo}
        colors={colors}
        navigationItems={navigationItems}
        handleLogout={handleLogout}
        toggleDarkMode={() => { }}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Top Navigation */}
        <AdminNav
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          colors={colors}
          currentTime={currentTime}
          toggleDarkMode={() => { }}
          handleLogout={handleLogout}
          darkMode={false}
          isMobile={isMobile}
          isTablet={isTablet}
          user={user}
        />

        {/* Content Area */}
        <div style={styles.contentArea}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;